import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import thunk from 'redux-thunk';
import { currentFilterReducer } from '@common/redux/reducers/CurrentFilterReducer';
import { negotiationCountReducer } from '@common/redux/reducers/NegotiationCountReducer';
import { customCountListReducer } from '@common/redux/reducers/CustomCountListReducer';
import { ReportTableRowReducer } from '@common/redux/reducers/ReportTableRowReducer';
import { SearchResultReducer } from '@common/redux/reducers/SearchResultReducer';
import { StoreSelectorReducer } from '@common/redux/reducers/StoreSelectorReducer';
import { NegotiationPeriodReducer } from '@common/redux/reducers/NegotiationPeriodReducer';
import { NegotiationPendingReducer } from '@common/redux/reducers/NegotiationPendingReducer';
import { SearchConditionReducer } from '@common/redux/reducers/SearchConditionReducer';
import { NegotiationNextActionReducer } from '@common/redux/reducers/NegotiationNextActionReducer';
import { NegotiationProbReducer } from '@common/redux/reducers/NegotiationProbReducer';
import { NegotiationResultReducer } from '@common/redux/reducers/NegotiationResultReducer';
import { RefreshReducer } from '@common/redux/reducers/RefreshReducer';
import { FilterListReducer } from '@common/redux/reducers/FilterListReducer';
import { LoadingStatusReducer } from '@common/redux/reducers/LoadingStatusReducer';
import { AppState } from '@common/redux/modules/store';
import { AgencyUserReducer, AgencyUserInfo } from '../reducers/UserReducer';
import { MailTopReducer } from '@common/redux/reducers/MailTopReducer';
import { MailDetailResultReducer } from '@common/redux/reducers/MailDetailResultReducer';
import { MailTemplateReducer } from '@common/redux/reducers/MailTemplateReducer';
import { MailTemplateDetailReducer } from '@common/redux/reducers/MailTemplateDetailReducer';
import { ApplyStaffReducer } from '@common/redux/reducers/ApplyStaffReducer';
import { MailTemplateApplyReducer } from '@common/redux/reducers/MailTemplateApplyReducer';
import { MailCreateReducer } from '@common/redux/reducers/MailCreateReducer';
import { MailSaveReducer } from '@common/redux/reducers/MailSaveReducer';
import { MailDraftReducer } from '@common/redux/reducers/MailDraftReducer';
import { MailAddressSelectReducer } from '@common/redux/reducers/MailAddressSelectReducer';
import { MailStopReducer } from '@common/redux/reducers/MailStopReducer';
import { MailDeliveryStopReducer } from '@common/redux/reducers/MailDeliveryStopReducer';
import { MailStopDoneReducer } from '@common/redux/reducers/MailStopDoneReducer';
import { SearchCategoryReducer } from '@common/redux/reducers/SearchCategoryReducer';
import { SorryMessageReducer } from '@common/redux/reducers/SorryMessageReducer';
import { NotificationStatusReducer } from '@common/redux/reducers/NotificationStatusReducer';
import { MasterInfoReducer } from '@common/redux/reducers/MasterInfoReducer';
import { searchBranchNextActionReducer } from '@common/redux/reducers/SearchBranchNextActionReducer';
import { searchBranchPendingReducer } from '@common/redux/reducers/SearchBranchPendingReducer';
import { searchBranchPeriodReducer } from '@common/redux/reducers/SearchBranchPeriodReducer';
import { searchBranchProbReducer } from '@common/redux/reducers/SearchBranchProbReducer';
import { searchBranchResultReducer } from '@common/redux/reducers/SearchBranchResultReducer';
import { searchBranchStatusReducer } from '@common/redux/reducers/SearchBranchStatusReducer';
import { searchBranchCommodityCategoryContractReducer } from '@common/redux/reducers/SearchBranchCommodityCategoryContractReducer';
import { DevConsoleReducer } from '@common/redux/reducers/DevConsoleReducer';
import { VisitReservationInfoReducer } from '@common/redux/reducers/VisitReservationInfoReducer';
import { CustomerInfoAllReducer } from '@common/redux/reducers/CustomerInfoAllReducer';
import { FilterReducer } from '@common/redux/reducers/StoreFilterReducer';
import { ModalConfirmReducer } from '@common/redux/reducers/ModalReducer';
import { FilterLinkedDataReducer } from '@common/redux/reducers/FilterLinkedDataReducer';
import { LocationHistoryReducer } from '@common/redux/reducers/LocationHistoryReducer';
import { LinkedDataResultReducer } from '@common/redux/reducers/LinkedDataResultReducer';
import { HearingInfoReducer } from '@common/redux/reducers/HearingInfoReducer';
import { SearchCustomerListConditionReducer } from '@common/redux/reducers/SearchCustomerListConditionReducer';

export type AgencyAppState = AppState & {
  loginUser: AgencyUserInfo;
};

const reducers = combineReducers<AgencyAppState>({
  filterStore: FilterReducer,
  modalConfirm: ModalConfirmReducer,
  negotiationCount: negotiationCountReducer,
  customCountList: customCountListReducer,
  negotiationPeriod: NegotiationPeriodReducer,
  negotiationProb: NegotiationProbReducer,
  negotiationNextAction: NegotiationNextActionReducer,
  reportTableRow: ReportTableRowReducer,
  currentFilterCondition: currentFilterReducer,
  negotiationPending: NegotiationPendingReducer,
  negotiationResult: NegotiationResultReducer,
  searchBranchPeriod: searchBranchPeriodReducer,
  searchBranchProb: searchBranchProbReducer,
  searchBranchNextAction: searchBranchNextActionReducer,
  searchBranchPending: searchBranchPendingReducer,
  searchBranchResult: searchBranchResultReducer,
  searchBranchStatus: searchBranchStatusReducer,
  searchBranchCommodityCategoryContract: searchBranchCommodityCategoryContractReducer,
  searchResult: SearchResultReducer,
  SearchCondition: SearchConditionReducer,
  loginUser: AgencyUserReducer,
  storeSelector: StoreSelectorReducer,
  currentRefresh: RefreshReducer,
  filterList: FilterListReducer,
  notificationStatus: NotificationStatusReducer,
  loadingStatus: LoadingStatusReducer,
  mailTop: MailTopReducer,
  mailDetail: MailDetailResultReducer,
  mailTemplate: MailTemplateReducer,
  mailTemplateDetail: MailTemplateDetailReducer,
  applyStaffList: ApplyStaffReducer,
  mailTemplateApply: MailTemplateApplyReducer,
  mailCreate: MailCreateReducer,
  mailSave: MailSaveReducer,
  mailDraft: MailDraftReducer,
  mailAddressSelect: MailAddressSelectReducer,
  mailStop: MailStopReducer,
  mailDeliveryStop: MailDeliveryStopReducer,
  mailStopDone: MailStopDoneReducer,
  masterInfo: MasterInfoReducer,
  searchCategory: SearchCategoryReducer,
  sorryMessage: SorryMessageReducer,
  devConsole: DevConsoleReducer,
  visitReservationInfo: VisitReservationInfoReducer,
  customerInfoAll: CustomerInfoAllReducer,
  filterLinkedData: FilterLinkedDataReducer,
  locationHistory: LocationHistoryReducer,
  linkedDataResult: LinkedDataResultReducer,
  hearingInfo: HearingInfoReducer,
  searchCustomerListCondition: SearchCustomerListConditionReducer,
});

const rootReducer = (state: AgencyAppState | undefined, action: any) => {
  if (action.type === 'SIGN_OUT') {
    // storeの初期化
    state = undefined;
  }

  return reducers(state, action);
};

const store = createStore(
  rootReducer,
  compose(
    applyMiddleware(thunk),
    (window as any).__REDUX_DEVTOOLS_EXTENSION__ ? (window as any).__REDUX_DEVTOOLS_EXTENSION__() : (f: any) => f,
  ),
);

export default store;
